import { utils } from "@vilocnv/allsetra-core";

/**
 * Declarative description of one CSV column.
 *
 * Deliberately separate from the `TableColumn` definitions the overviews
 * render with: those return JSX (badges, tooltips) and screen placeholders
 * such as `-` or `N/A`, neither of which belongs in a file a spreadsheet has
 * to parse.
 */
/**
 * The `t` a page already holds. Typed loosely on purpose: `react-i18next`'s
 * own signature is generic over the namespace tuple, and the column maps only
 * ever call it with a key and an `ns`.
 */
export type CsvTranslator = (
  key: string,
  options?: Record<string, unknown>
) => string;

export interface CsvColumn<T> {
  header: string;
  value: (row: T) => string | number | null | undefined;
}

/** `react-csv`'s header shape. */
export interface CsvHeader {
  label: string;
  key: string;
}

export type CsvRow = Record<string, string | number>;

/**
 * Column keys are positional (`col0`, `col1`, ...) rather than the header
 * text, so two columns sharing a header — or a header containing characters
 * that are awkward as an object key — can never collapse into one another.
 */
const columnKey = (index: number): string => `col${index}`;

export const buildCsvHeaders = <T>(columns: CsvColumn<T>[]): CsvHeader[] =>
  columns.map((column, index) => ({
    label: column.header,
    key: columnKey(index)
  }));

/**
 * An absent value becomes an empty cell — never `-`, `N/A` or `undefined`.
 * Every row is built from the same column list, so all rows carry the same
 * keys and the file keeps a constant column count.
 */
export const buildCsvRows = <T>(
  data: T[] | null | undefined,
  columns: CsvColumn<T>[]
): CsvRow[] =>
  (data ?? []).map((row) =>
    columns.reduce<CsvRow>((accumulator, column, index) => {
      const value = column.value(row);

      accumulator[columnKey(index)] =
        value === null || value === undefined ? "" : value;

      return accumulator;
    }, {})
  );

/**
 * Formats a date with the same helper the tables use, so a cell in the file
 * reads exactly like the cell on screen.
 */
export const formatCsvDate = (
  date: string | Date | null | undefined
): string => {
  if (date === null || date === undefined || date === "") return "";

  return utils.formatDate({ date }) || "";
};

/**
 * One decimal, no unit — the unit lives in the column header.
 *
 * Returned as the string `toFixed` produces rather than converted back to a
 * number, which would turn the table's `17.0 km` into `17`. Every cell is
 * quoted anyway, and a spreadsheet still reads `17.0` as a number.
 */
export const formatCsvKilometers = (
  kilometers: number | null | undefined
): string => {
  if (
    kilometers === null ||
    kilometers === undefined ||
    Number.isNaN(kilometers)
  ) {
    return "";
  }

  return kilometers.toFixed(1);
};

const pad = (value: number): string => String(value).padStart(2, "0");

/**
 * `<prefix>-YYYY-MM-DD.csv`, dated in the user's own timezone so the name
 * matches the day they clicked export on.
 */
export const buildCsvFilename = (
  prefix: string,
  date: Date = new Date()
): string =>
  `${prefix}-${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
    date.getDate()
  )}.csv`;

/**
 * Doubles embedded double quotes, per RFC 4180.
 *
 * `react-csv` wraps every field in quotes but does not escape quotes *inside*
 * a value (see `joiner` in react-csv's core), so `Pool car, "shared"` would be
 * written as `"Pool car, "shared""` and read back as `Pool car, shared` — the
 * quotes silently dropped, and a value ending in a quote able to shift the
 * remaining columns.
 *
 * Kept out of `buildCsvRows` and applied at the point the rows are handed to
 * `CSVLink`, so it can be deleted along with that dependency if the export
 * ever moves to a serializer that escapes correctly.
 */
export const escapeCsvRows = (rows: CsvRow[]): CsvRow[] =>
  rows.map((row) =>
    Object.entries(row).reduce<CsvRow>((accumulator, [key, value]) => {
      accumulator[key] =
        typeof value === "string" ? value.replace(/"/g, '""') : value;

      return accumulator;
    }, {})
  );

/**
 * Puts the unit in the column header, since the cells carry a bare number.
 *
 * The table shows the unit inside the cell (`1234.6 km`); a spreadsheet wants
 * the number numeric, so the unit moves up into the caption instead of being
 * lost. Applied on top of the translated heading rather than baked into the
 * translation, so a new language gets it for free.
 */
export const withKilometreUnit = (header: string): string => `${header} (km)`;
